import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  useLogin,
  useGetOTP,
  useVerifyCredentials,
} from "@/hooks/use-mutations";
import Throbber from "@/components/throbber";
import { useState } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import LoginBg from "@/components/login-bg";
import Logo from "@/components/logo";
import { handleError } from "@/lib/helper";
import ResendOtpButton, {
  otpAltActionBtnOnLight,
} from "@/components/resend-otp-button";

const schema = z.object({
  email: z.string().email("Invalid email address").min(1, "Email is required"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[\W_]/, "Password must contain at least one special character"),
  otp: z.string().min(6, "Authenticator code must be 6 digits"),
});

type FormData = z.infer<typeof schema>;

const Login = () => {
  const [searchParams] = useSearchParams();
  const param = searchParams.get("o");

  const [onboardingIsOpen, setOnboardingIsOpen] = useState(param !== "0");

  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [otpMethod, setOtpMethod] = useState<TwoFactorMethod>("email");
  const [isSwitchingMethod, setIsSwitchingMethod] = useState(false);
  // Whether this account has authenticator 2FA set up at all, from
  // check-credentials — hides "use alternative method" when there's no
  // alternative to switch to.
  const [hasAuthenticator, setHasAuthenticator] = useState(false);
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
      otp: "",
    },
  });

  const { mutateAsync: getOtp, isPending: isGettingOtp } = useGetOTP();
  const { mutateAsync: verify, isPending: isVerifying } =
    useVerifyCredentials();

  const { mutate: login, isPending: isLoggingIn } = useLogin({
    onSuccess: () => {
      navigate("/dashboard");
    },
  });
  const onSubmit = (data: FormData) => {
    login({ ...data, otpMethod });
  };

  const handleSwitchOtpMethod = async () => {
    if (otpMethod === "authenticator") {
      setIsSwitchingMethod(true);
      try {
        await getOtp({
          emailAddress: form.getValues("email"),
          purpose: "login",
        });
        form.setValue("otp", "");
        setOtpMethod("email");
      } catch {
        // The mutation's own onError already toasts.
      } finally {
        setIsSwitchingMethod(false);
      }
    } else {
      form.setValue("otp", "");
      setOtpMethod("authenticator");
    }
  };

  const handleContinue = async () => {
    try {
      const isValid = await form.trigger(["email", "password"]);
      if (!isValid) return;
      const verifyResponse = await verify({
        email: form.getValues("email"),
        password: form.getValues("password"),
      });
      if (!verifyResponse.data.valid) return;
      // Fail open: only hide the switch option when the backend explicitly
      // says there's no authenticator to switch to.
      setHasAuthenticator(
        verifyResponse.data.twoFactorAuthenticatiorEnabled !== false,
      );
      // An authenticator account has no email code to send, so go straight to
      // code entry.
      if (verifyResponse.data.otpMethod === "authenticator") {
        setOtpMethod("authenticator");
        setStep(2);
        return;
      }
      const otpResponse = await getOtp({
        emailAddress: form.getValues("email"),
        purpose: "login",
      });

      setOtpMethod(otpResponse.data.type ?? "email");
      setStep(2);
    } catch (error: any) {
      if (error.response.data.error === "Incorrect password") {
        form.setError("password", {
          type: "required",
          message: "Incorrect Password",
        });
      }
      if (
        error.response.data.error === "No account with the provided email found"
      ) {
        form.setError("email", {
          type: "required",
          message: "Email not found",
        });
      }
      handleError(error);
    }
  };

  // Enter continues on step 1 and submits the login on step 2.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (step === 1) {
      e.preventDefault();
      if (!isGettingOtp && !isVerifying) handleContinue();
      return;
    }
    return form.handleSubmit(onSubmit)(e);
  };
  return (
    <div className="text-auth-text  relative h-full w-full overflow-hidden">
      {onboardingIsOpen && (
        <div className="absolute inset-0 z-10 lg:hidden">
          <LoginBg />
          <div className="absolute right-6 bottom-10">
            <div className="bg-auth-ring absolute right-1/2 bottom-1/2 size-18 translate-x-1/2 translate-y-1/2 animate-ping rounded-full" />
            <Button
              onClick={() => setOnboardingIsOpen(false)}
              variant="default"
              className="bg-auth-arrow relative z-10 size-14 rounded-full text-black"
            >
              <ArrowRight size={25} />
            </Button>
          </div>
        </div>
      )}
      <div className="relative flex min-h-dvh">
        {/* Left panel. `aspect-[617/832]` matches the artwork's viewBox so the
            column is the correct width before the image decodes, and
            `bg-auth-panel` fills it in the meantime. */}
        <div className="bg-auth-panel fixed inset-y-0 left-0 hidden aspect-617/832 h-screen lg:block">
          <img
            src="/images/auth-image2.svg"
            className="h-full w-auto"
            alt="The New Standard of Digital Finance. Easily create virtual cards to handle every online purchase, bill, and subscription."
          />
        </div>
        {/* The layer above is `fixed` and out of flow, so this reserves the
            column width. Same ratio, no second image download. */}
        <div className="hidden aspect-617/832 h-screen lg:block" />
        <div className="bg-auth-surface relative flex grow flex-col items-center justify-center py-10">
          <form
            onSubmit={handleFormSubmit}
            className="relative flex w-[320px] flex-col items-start md:w-[400px] lg:w-full lg:max-w-sm"
          >
            <Logo className="text-auth-action w-32" />

            <div className="bg-auth-track border-auth-stroke mt-4 inline-flex gap-2 rounded border p-0.5">
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  `inline-flex w-24 items-center justify-center rounded px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-auth-action text-auth-on-action" : "text-auth-muted hover:text-auth-text"}`
                }
              >
                Login
              </NavLink>
              <NavLink
                to="/signup"
                className={({ isActive }) =>
                  `inline-flex w-24 items-center justify-center rounded px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-auth-action text-auth-on-action" : "text-auth-muted hover:text-auth-text"}`
                }
              >
                Sign Up
              </NavLink>
            </div>

            <Form {...form}>
              <div className="mt-2.5 flex w-full flex-col gap-2.5 lg:min-w-sm">
                {/* Step 1 */}
                {step === 1 && (
                  <>
                    <h1 className="mt-4 text-2xl font-semibold">
                      Welcome back!
                    </h1>
                    <p>Log in with your Email and password</p>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="text-sm">
                            Email address
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="border-auth-stroke placeholder:text-auth-muted rounded"
                              type={"email"}
                              {...field}
                              onChange={(e) => {
                                const cleaned = e.target.value
                                  .replace(/\s+/g, "")
                                  .toLowerCase();
                                form.clearErrors("email");
                                field.onChange(cleaned);
                              }}
                              placeholder="Enter your email address"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="text-sm">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded"
                                type={showPassword ? "text" : "password"}
                                {...field}
                                onChange={(e) => {
                                  form.clearErrors("password");
                                  field.onChange(e.target.value);
                                }}
                                placeholder="Enter your password"
                              />
                              <Button
                                type="button"
                                variant={"ghost"}
                                className="text-auth-muted absolute right-0"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <Eye /> : <EyeOff />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Link
                      className="text-auth-link hover:text-auth-link/80 -mt-1 text-end text-sm font-medium"
                      to="/reset-password"
                    >
                      Forgot password
                    </Link>

                    <Button
                      className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-6 h-11 w-full rounded"
                      type="submit"
                      isLoading={isGettingOtp || isVerifying}
                      disabled={isGettingOtp || isVerifying}
                    >
                      Continue
                    </Button>

                    <p className="mt-4 text-center text-sm">
                      Don&apos;t have an account?{" "}
                      <Link
                        className="text-auth-link hover:text-auth-link/80 mx-auto"
                        to="/signup"
                      >
                        Sign up
                      </Link>
                    </p>
                  </>
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <>
                    <Button
                      type="button"
                      className="absolute -top-32 -left-2 size-8"
                      variant={"ghost"}
                      onClick={() => setStep(1)}
                    >
                      <ArrowLeft />
                    </Button>
                    {otpMethod === "authenticator" ? (
                      <>
                        <h1 className="mt-6 text-2xl font-semibold">
                          2 Factor Authentication
                        </h1>
                        <p className="text-auth-muted text-sm leading-3.5">
                          Enter the 6-digit code from your authenticator app to
                          continue.
                        </p>
                      </>
                    ) : (
                      <>
                        <h1 className="mt-6 text-2xl font-semibold">
                          Let’s verify your Email
                        </h1>
                        <p className="text-auth-muted text-sm leading-3.5">
                          We’ve sent a 6-digit code to your email, enter the
                          code below to verify
                        </p>
                      </>
                    )}

                    <div className="mt-6 flex w-full flex-col gap-2.5">
                      <FormField
                        control={form.control}
                        name="otp"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Enter verification code</FormLabel>
                            <FormControl>
                              <InputOTP
                                autoFocus
                                autoComplete="off"
                                pattern={REGEXP_ONLY_DIGITS}
                                maxLength={6}
                                {...field}
                              >
                                <InputOTPGroup className="*:border-auth-otp-stroke gap-3 *:!rounded *:border *:shadow-none">
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {otpMethod === "email" && (
                        <ResendOtpButton
                          mode="light"
                          onResend={() =>
                            getOtp({
                              emailAddress: form.getValues("email"),
                              purpose: "login",
                            })
                          }
                        />
                      )}

                      <Button
                        disabled={isLoggingIn}
                        className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-6 h-11 w-full rounded"
                        type="submit"
                      >
                        {isLoggingIn ? <Throbber /> : "Login"}
                      </Button>

                      {(otpMethod === "authenticator" || hasAuthenticator) && (
                        <button
                          type="button"
                          onClick={handleSwitchOtpMethod}
                          disabled={isSwitchingMethod}
                          className={otpAltActionBtnOnLight}
                        >
                          {isSwitchingMethod ? (
                            <Throbber />
                          ) : otpMethod === "authenticator" ? (
                            "Use email OTP instead"
                          ) : (
                            "Use authenticator app instead"
                          )}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Form>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
